"use client"

import * as React from "react"

import { cn } from "@/lib/utils"
import { Button } from "@/components/ui/button"
import { Input } from "@/components/ui/input"
import { COLORES_DE_ICONO, EMOJIS_RAPIDOS, ICONOS_DISPONIBLES } from "@/lib/colores-de-icono"


interface Visual {
  icon?: string
  colorClass?: string
  coverUrl?: string
}

interface SelectorVisualProps {
  visual: Visual
  onVisualChange: (visual: Visual) => void
  /** Como se pinta cada icono. Lo pone quien llama, porque el agente y la
   *  herramienta tienen su propio pintor. */
  renderIcono: (id: string, colorClass?: string) => React.ReactNode
  /** Se llama al elegir, para que quien abrio el desplegable lo cierre. */
  onElegido?: () => void
  columnasIconos?: number
  columnasEmojis?: number
  /** Los iconos entre los que elegir. Por defecto los del sistema; una vista
   *  que pinte mas -los filtros guardados ensenan 39- pasa los suyos, y con
   *  ellos su `renderIcono`. */
  iconos?: readonly string[]
}

/**
 * Elegir con que se ve un agente o una herramienta: su color, su icono o un
 * emoji.
 *
 * Estaba escrito dos veces DENTRO del mismo fichero -una version compacta y
 * otra expandida, identicas salvo por el numero de columnas-, y sus ocho
 * `<button>` crudos eran ocho de los diecinueve que tenia Agent Studio.
 *
 * El elemento crudo se queda aqui, que es donde esta bien: una muestra de color
 * o un icono para elegir no es el componente `Button` -lo unico que tiene que
 * ensenar es el color o el icono-, pero si es un boton: se pulsa y responde al
 * teclado.
 */
export function SelectorVisual({
  visual,
  onVisualChange,
  renderIcono,
  onElegido,
  columnasIconos = 8,
  columnasEmojis = 6,
  iconos = ICONOS_DISPONIBLES,
}: SelectorVisualProps) {
  const [emoji, setEmoji] = React.useState('')

  const elegir = (cambio: Visual) => {
    onVisualChange({ ...visual, coverUrl: undefined, ...cambio })
    onElegido?.()
  }

  return (
    <>
      <div className="mb-2 flex items-center gap-2">
        {COLORES_DE_ICONO.map(({ texto, fondo }) => (
          <button
            key={texto || 'none'}
            type="button"
            aria-label={texto || 'sin color'}
            onClick={() => onVisualChange({ ...visual, colorClass: texto || undefined })}
            className={cn(
              'h-5 w-5 rounded-full border',
              fondo,
              visual.colorClass === texto && 'ring-2 ring-primary',
            )}
          />
        ))}
      </div>

      <div
        className="grid max-h-44 gap-2 overflow-auto overscroll-contain"
        style={{ gridTemplateColumns: `repeat(${columnasIconos}, minmax(0, 1fr))` }}
      >
        {iconos.map((id) => (
          <button
            key={id}
            type="button"
            aria-label={id}
            onClick={() => elegir({ icon: id })}
            className={cn(
              'flex h-8 w-8 items-center justify-center rounded-md border hover:bg-muted',
              visual.icon === id && 'bg-primary text-primary-foreground',
            )}
          >
            {renderIcono(id, visual.colorClass)}
          </button>
        ))}
      </div>

      <div
        className="mt-2 grid gap-2"
        style={{ gridTemplateColumns: `repeat(${columnasEmojis}, minmax(0, 1fr))` }}
      >
        {EMOJIS_RAPIDOS.map((e) => (
          <button
            key={e}
            type="button"
            onClick={() => { setEmoji(e); elegir({ icon: `emoji:${e}` }) }}
            className={cn(
              'h-8 w-8 rounded-md border text-sm hover:bg-muted',
              visual.icon === `emoji:${e}` && 'border-primary/30 bg-primary/10',
            )}
          >
            {e}
          </button>
        ))}
      </div>

      <div className="mt-2 flex items-center gap-2">
        <Input
          value={emoji}
          onChange={(ev) => setEmoji(ev.target.value)}
          placeholder="Pega un emoji"
          className="h-8 text-sm"
        />
        <Button
          type="button"
          size="xs"
          variant="secondary"
          className="shrink-0"
          onClick={() => {
            if (!emoji.trim()) return
            elegir({ icon: `emoji:${emoji.trim()}` })
          }}
        >
          Usar
        </Button>
      </div>
    </>
  )
}
